import { useTranslation } from 'react-i18next';
import { useMemo, memo } from 'react';

import useEvents from 'lib/hooks/useEvents';
import CabinTypesTap from 'lib/Tracking/Srp/SearchFilters/TruckOptionsFilter/CabinTypeTap';
import { useTruckOptionsFilter } from 'lib/hooks/useSrpFilters';

import LabeledCheckBox from 'components/Shared/SearchFilter/CheckBox/LabeledCheckBox';
import SearchFilterSubHeader from 'components/Shared/SearchFilter/SearchFilterSubHeader';
import FacetCount, { styles as facetCountStyles } from 'components/Shared/SearchFilter/FacetCount';

import { CabinType } from 'types/srpFilters';
import { useContext } from 'react';
import SrpEventContext from 'contexts/SrpEventContext';

const cabinTypes: CabinType[] = ['extended', 'regular', 'crew'];

function CabinTypeFilter({ cabinFacets }: { cabinFacets?: Record<string, number> }): JSX.Element {
  const logger = useEvents();
  const { sourcePage } = useContext(SrpEventContext);
  const { t } = useTranslation();
  const { value, setValue } = useTruckOptionsFilter();

  const cabinTypeOptions = useMemo(() => {
    return cabinTypes.map((cabinType) => {
      const cabinTypeLabel = t(`srp.filters.truckOptions.${cabinType}`);
      const hasZeroFacets =
        cabinFacets?.[cabinType] === 0 || cabinFacets?.[cabinType] === undefined;
      const rawCount = hasZeroFacets ? 0 : (cabinFacets?.[cabinType] as number);

      return {
        cabinType,
        cabinTypeLabel,
        hasZeroFacets,
        rawCount,
        isChecked: value.cabinType?.[cabinType] ?? false,
      };
    });
  }, [cabinFacets, t, value.cabinType]);

  const onTap = useMemo(
    () =>
      (label: string, isSelected: boolean): void => {
        setValue({ ...value, cabinType: { ...value?.cabinType, [label]: isSelected } });
        logger.log(CabinTypesTap(label, sourcePage));
      },
    [value, setValue, logger, sourcePage]
  );

  return (
    <>
      <SearchFilterSubHeader subTitle={t(`srp.filters.truckOptions.cabinType`)} />
      {cabinTypeOptions.map(({ cabinType, cabinTypeLabel, hasZeroFacets, rawCount, isChecked }) => (
        <div key={cabinType} className="cabin-type-checkbox-container">
          <LabeledCheckBox
            label={cabinTypeLabel}
            aria-label={`${cabinTypeLabel}-label`}
            isChecked={isChecked}
            onClick={(isSelected: boolean): void => onTap(cabinType, isSelected)}
            style={{ width: 'auto', paddingRight: '5px' }}
            disabled={hasZeroFacets}
          />
          <FacetCount count={rawCount} hasZeroFacets={hasZeroFacets} />
        </div>
      ))}
      <style jsx>{`
        .cabin-type-checkbox-container {
          display: flex;
          align-items: center;
        }
        ${facetCountStyles}
      `}</style>
    </>
  );
}

export default memo(CabinTypeFilter);
